import { createApiClient } from "@mozaic/api-client";
import { env } from "@/lib/env";
import { CSRF_HEADER } from "@/shared/auth/constants";
import { csrfToken, isUnsafeMethod } from "@/shared/auth/csrf";

// Created per call: the client binds `fetch` at creation, and construction is cheap.
export function api() {
  const client = createApiClient(env.apiUrl);
  client.use({
    onRequest({ request }) {
      const token = isUnsafeMethod(request.method) ? csrfToken() : null;
      if (token) request.headers.set(CSRF_HEADER, token);
      return request;
    },
  });
  return client;
}

export const MAINTENANCE_MODE_CODE = "maintenance_mode";
/** Code of an ApiError raised when a response body does not match the contract the client was built for. */
export const CLIENT_SCHEMA_CODE = "client_schema_mismatch";

/** RFC 9457 problem body as emitted by the API (`ProblemDetails` in core/errors.py; not in the OpenAPI schema). */
export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  code: string;
  detail: string;
  instance?: string | null;
  request_id?: string | null;
}

/** Narrows an unknown error body to a ProblemDetails, or null if it does not have the required fields. */
export function parseProblem(body: unknown): ProblemDetails | null {
  if (typeof body !== "object" || body === null) return null;
  const { type = "about:blank", title, status, code, detail, instance, request_id } = body as Record<string, unknown>;
  if (typeof title !== "string" || typeof status !== "number" || typeof code !== "string" || typeof detail !== "string") {
    return null;
  }
  return {
    type: typeof type === "string" ? type : "about:blank",
    title,
    status,
    code,
    detail,
    instance: typeof instance === "string" ? instance : null,
    request_id: typeof request_id === "string" ? request_id : null,
  };
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number | null = null,
    readonly code: string | null = null,
    /** The parsed problem body, when the API sent one. */
    readonly problem: ProblemDetails | null = null,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** Correlation id to quote to support. */
  get requestId(): string | null {
    return this.problem?.request_id ?? null;
  }

  /** The platform is in upgrade/restore maintenance and refuses side-effecting requests. */
  get isMaintenance(): boolean {
    return this.status === 503 && this.code === MAINTENANCE_MODE_CODE;
  }
}

export function isMaintenanceError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.isMaintenance;
}

/** Turns an openapi-fetch result into data, or throws a typed ApiError (problem+json aware). */
export async function unwrap<T>(
  request: Promise<{ data?: T; error?: unknown; response: Response }>,
  /** Lets an endpoint treat a specific error response as a valid result (e.g. a 503 health report). */
  accept?: (error: unknown, response: Response) => T | undefined,
): Promise<T> {
  let result;
  try {
    result = await request;
  } catch {
    throw new ApiError("The API could not be reached. Check your connection and try again.");
  }
  if (result.data !== undefined) return result.data;
  const accepted = accept?.(result.error, result.response);
  if (accepted !== undefined) return accepted;
  const problem = parseProblem(result.error);
  const loose = (typeof result.error === "object" && result.error ? result.error : {}) as Record<string, unknown>;
  const message =
    problem?.detail ||
    problem?.title ||
    (typeof loose.detail === "string" ? loose.detail : typeof loose.title === "string" ? loose.title : "") ||
    `The API returned an unexpected response (${result.response.status}).`;
  throw new ApiError(
    message,
    result.response.status,
    problem?.code ?? (typeof loose.code === "string" ? loose.code : null),
    problem,
  );
}
